import { type HttpClient, HttpErrorResponse } from '@angular/common/http';
import { getCsrfToken } from '@frontendlib/common/utils/auth';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { firstValueFrom, timeout } from 'rxjs';
import { type SessionCall } from '@sharedlib/sheets-editor/editor-session';

/** A session call that takes longer than this counts as failed; the next heartbeat tries again. */
export const SESSION_CALL_TIMEOUT_MS = 10_000;

/** AM-46: the open names the workbook; every later call names only the session. */
const openUrl = (workbookId: string) => `${environment.baseUrl}/api/sheets/workbooks/${workbookId}/sessions`;
const sessionUrl = (sessionId: string) => `${environment.baseUrl}/api/sheets/sessions/${sessionId}`;

/**
 * One AM-46 call. Resolves with the HTTP status, 0 below HTTP; never rejects. `tenantId` is the
 * workbook's, named on the request as `putSnapshot` names it: a heartbeat or close can go after a
 * switch to another space.
 */
export async function sendSessionCall(http: HttpClient, workbookId: string, sessionId: string, call: SessionCall, tenantId?: string | null): Promise<number> {
  const options = { observe: 'response' as const, context: owningTenantContext(tenantId) };
  const request = call.kind === 'open' ? http.post(openUrl(workbookId), call.body, options) : http.put(sessionUrl(sessionId), call.body, options);
  try {
    return (await firstValueFrom(request.pipe(timeout(SESSION_CALL_TIMEOUT_MS)))).status;
  } catch (error) {
    return error instanceof HttpErrorResponse ? error.status : 0;
  }
}

/**
 * The close. On `pagehide` it is a beacon, the one request a page going away still gets out.
 * A beacon cannot set headers, so the CSRF token goes in a `text/plain` body (no CORS preflight),
 * where the gateway looks for it (AM-46), and `space_id` goes in the URL, which the spaces
 * interceptor adds for every other call. Otherwise, or when the browser refuses the beacon, an
 * ordinary request.
 */
export function closeSession(
  http: HttpClient,
  sessionId: string,
  { beacon, sharedSpaceId, tenantId }: { beacon: boolean; sharedSpaceId: string | null; tenantId?: string | null },
): void {
  const url = `${sessionUrl(sessionId)}/close`;
  if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const query = sharedSpaceId ? `?space_id=${encodeURIComponent(sharedSpaceId)}` : '';
    const body = new Blob([JSON.stringify({ csrfToken: getCsrfToken() })], { type: 'text/plain' });
    if (navigator.sendBeacon(`${url}${query}`, body)) return;
  }
  http.post(url, {}, { context: owningTenantContext(tenantId) }).subscribe({ error: () => undefined });
}

/**
 * The ack of a navigation command (§6.1 `sheets.nav.ack`, over HTTP under AM-46), or of a UI command with what the
 * editor shows after it (`state`, the MCP contract's `sheets_ui`). Best effort.
 */
export function postNavAck(
  http: HttpClient,
  sessionId: string,
  ack: { commandId: string; ok: boolean; error: string | null; state?: Record<string, unknown> },
  tenantId?: string | null,
): void {
  http.post(`${sessionUrl(sessionId)}/nav-ack`, ack, { context: owningTenantContext(tenantId) }).subscribe({ error: () => undefined });
}
